import { VacancyProps, VacancySliderBlok } from "storyblok/components";
import { storyblokEditable } from "@storyblok/react";
import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

import {
    ARROW_RIGHT,
    AnimationStyle,
    FramerMotionWrapper,
    Icon
} from "components/ui";
import { VacancyCard } from "components/vacancies";

import {
    StyledContainer,
    StyledLink,
    StyledScrollContainer,
    Title,
    Top,
    Wrapper
} from "./VacancySlider.styled";

import { filterVacanciesByKey } from "helpers/util/filterVacanciesByDepartment";
import { sortVacancies } from "helpers/util/sortVacancies";
import { useMediaQueryContext } from "helpers/hooks";

type Props = {
    blok: VacancySliderBlok;
    vacancies: Array<VacancyProps>;
    zzpVacancies: Array<VacancyProps>;
    jobFunction?: string;
    departments?: Array<{ label: string }>;
};

function generateVacancyCard(v: VacancyProps, zzp: boolean, index: number) {
    if (!v) return <></>;

    const href = `/zorgprofessionals/${zzp ? "opdrachten" : "vacatures"}/${
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        zzp ? v?.id : v?.slug
    }`;
    const category = zzp ? v?.start_date : v?.department;
    const hours = zzp
        ? v?.uren_per_week
        : v?.min_hours && v?.max_hours
        ? `${v?.min_hours}-${v?.max_hours} uur`
        : null;
    const city = zzp ? v?.client?.town : v?.city;
    const education = zzp ? v?.niveau : v?.education_code;
    let description = v?.description || v?.inhoud || "";

    if (!zzp) description = JSON.parse(description).intro;

    return (
        <VacancyCard
            className="slider-card"
            key={`${JSON.stringify(v)}-${index}`}
            title={v.title}
            href={href}
            category={category}
            hours={hours}
            description={description}
            education={education}
            region={`Regio ${city || ""}`}
        />
    );
}

// When checking for valid department, we have some exceptions that need to be handled
function getVacancySpecifiedDepartment(department?: string) {
    if (department === "Verpleegkundige") return "Verpleegkundigen";
    if (department === "Basisarts") return "Basisartsen";

    return department;
}

const VACANCY_OVERVIEW_LINK = `/zorgprofessionals/vacatures?departments=`;

function renderVacancySliderLink(
    link?: string,
    department?: string,
    jobFunction?: string
) {
    // const usableDepartment = getVacancySpecifiedDepartment(department);

    // jobFunction has highest priority, passed down from StoryDetailPage (ervaringen/[slug].tsx)
    if (jobFunction)
        return (
            VACANCY_OVERVIEW_LINK + getVacancySpecifiedDepartment(jobFunction)
        );

    // On any page that isn't a StoryDetailPage, if link is set, return link
    if (link) return link;

    // If no link and department is set, return link to vacancies with selected department
    if (department) {
        return VACANCY_OVERVIEW_LINK + department;
    }

    // If no link and no department is set, return link to vacancies
    return `/vacatures`;
}

export function VacancySlider({
    blok,
    vacancies,
    zzpVacancies,
    jobFunction,
    departments
}: Props): JSX.Element {
    const { isMobileView, isDesktopView } = useMediaQueryContext();
    const { title, type, link, department, employmentType } = blok;

    const [boxHeight, setBoxHeight] = useState(300);

    const activeVacancies =
        type === "zzp" ? sortVacancies(zzpVacancies) : sortVacancies(vacancies);

    const filteredCappedVacancies = useMemo(() => {
        const filteredVacancies = filterVacanciesByKey({
            allVacancies: activeVacancies,
            keys: { department, employmentType }
        });

        const uniqueVacancies = filteredVacancies.reduce(
            (acc: Array<VacancyProps>, curr: VacancyProps) => {
                if (acc.find(v => v.slug === curr.slug)) {
                    return acc;
                }

                return [...acc, curr];
            },
            []
        );

        return uniqueVacancies.slice(0, 10);
    }, [activeVacancies, department, employmentType]);

    useEffect(() => {
        if (isDesktopView || isMobileView) {
            const allSlides = document.querySelectorAll("[class*=slider-card]");
            const boxHeights =
                allSlides &&
                Array.from(allSlides)?.map(
                    s => s.getBoundingClientRect().height
                );
            const maxHeight = Math.min(...(boxHeights || []));
            setBoxHeight(maxHeight);
        }
    }, [isDesktopView, isMobileView]);

    if (!filteredCappedVacancies.length) return <></>;

    // Check whether this department is linkable to the vacancy overview page
    const linkableJobFunction = departments
        ?.map(dep => dep.label)
        ?.find(dep => dep === getVacancySpecifiedDepartment(jobFunction));

    const renderedLink = renderVacancySliderLink(
        link,
        department,
        linkableJobFunction
    );

    return (
        <FramerMotionWrapper
            playWhenInView
            animationType={AnimationStyle.FadeInFromBottom}
            duration={0.5}
            delay={0.1}
        >
            <Wrapper {...storyblokEditable(blok)}>
                <StyledContainer boxHeight={isDesktopView ? 300 : boxHeight}>
                    <Top>
                        <Title>{title}</Title>
                    </Top>

                    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                    {/* @ts-ignore */}
                    <StyledScrollContainer vertical={false} horizontal>
                        {filteredCappedVacancies &&
                            filteredCappedVacancies.map((v, index) =>
                                generateVacancyCard(v, type === "zzp", index)
                            )}
                    </StyledScrollContainer>

                    <Link legacyBehavior href={renderedLink} passHref={true}>
                        <StyledLink href="#">
                            <span>
                                Bekijk alle{" "}
                                {type === "zzp" ? "opdrachten" : "vacatures"}
                            </span>
                            <Icon
                                icon={ARROW_RIGHT}
                                size={24}
                                color="var(--color-black)"
                            />
                        </StyledLink>
                    </Link>
                </StyledContainer>
            </Wrapper>
        </FramerMotionWrapper>
    );
}
